<template>
  <app-loader
    :action="loadUsers"
    class="d-flex flex-column ga-4 align-center"
  >
    <div v-if="!small || selectedUserId == $auth.user!.id">
      <h2
        class="text-center mb-8"
        v-if="!small"
      >
        <template v-if="$route.query['after-login']">
          Let’s log you in. Just choose your profile to get started.
        </template>
        <template v-else>
          Welcome Back
        </template>
      </h2>
      <UserSelector
        v-model="selectedUserId"
        :users="users"
        :disabled="!!loading || otpLogin"
        :scroll="small"
        :show-impersonate="showImpersonate"
      />
    </div>

    <template v-if="!small || selectedUserId != $auth.user!.id">
      <div
        v-if="otpLogin"
        class="text-center"
      >
        <span class="text-secondary">We sent a code to mobile number: </span>
        <b>{{ formatPhone(selectedUser?.phone) }}</b>
      </div>

      <div
        class="text-center"
        v-if="selectedUser?.has_pin"
      >
        <h6 v-if="!small">
          {{ otpLogin ? 'Enter your OTP' : 'Please enter your pin' }}.
        </h6>

        <v-otp-input
          base-color="primary"
          v-model="pin"
          :error="!!error"
          length="4"
          width="250"
          @change="error = ''"
          @keydown.enter="submit()"
          @finish="submit()"
        />
        <div
          class="text-red text-center"
          v-if="error"
          v-text="error"
        />

        <div
          v-if="!otpLogin && selectedUser?.phone"
          class="text-link"
          @click="startOtpLogin"
        >
          Forgot your pin?
        </div>
        <div
          v-if="otpLogin"
          class="text-center mb-4"
        >
          <div v-if="resendSeconds == 0">
            <span class="text-secondary">Didn’t get a code?</span> <span
              class="text-link"
              @click="resend"
            >Click to resend</span>
          </div>
          <div
            v-else
            class="text-secondary"
          >
            Resent! Can resend again in {{ resendSeconds }}
          </div>
        </div>

        <v-btn-primary
          class="mt-4 px-14"
          :loading="loading === 'login'"
          @click="submit()"
        >
          Submit
        </v-btn-primary>

        <div
          v-if="!small && otpLogin"
        >
          <v-btn-secondary
            class="mt-4 px-16 mt-4"
            @click="otpLogin = false; pin = ''"
          >
            Back
          </v-btn-secondary>
        </div>
      </div>
      <div
        v-else
        class="text-center"
      >
        <div class="text-secondary mb-16">
          {{ small ? 'Logging in...' : 'No pin required.' }}
        </div>

        <v-btn-primary
          class="px-16"
          :loading="loading === 'login'"
          @click="submit(true)"
        >
          Proceed
        </v-btn-primary>
      </div>
    </template>

    <pin-dialog
      v-model="dialogs['resetPin']"
      :key="dialogKeys['resetPin']"
      :user="$auth.user!"
      reset
      @close="navigateToCalendar"
    />
  </app-loader>
</template>
<script lang="ts">
import AppLoader from "@/components/AppLoader.vue"
import UserSelector from "@/components/UserSelector.vue"
import validators from "@/mixins/validators"
import format from "@/mixins/format"
import dialog from "@/mixins/dialog"
import PinDialog from "@/components/PinDialog.vue"
import type {PropType} from "vue"

export default {
  name: 'AccountLogin',
  components: {PinDialog, AppLoader, UserSelector},
  props: {
    modelValue: {
      type: Object as PropType<App.Models.User | null>,
      default: null
    },
    small: Boolean,
    showImpersonate: Boolean,
  },
  mixins: [validators, format, dialog],
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      loading: '',
      error: '',
      users: [] as App.Models.User[],
      selectedUserId: null as number | null,
      pin: '',
      otpLogin: false,
      resendSeconds: 0,
      resendTimer: null as any,
      userIdPreSelected: false,
    }
  },
  mounted() {
    this.selectedUserId = this.$auth.user!.id

    this.resendTimer = setInterval(() => {
      if (this.resendSeconds > 0) {
        this.resendSeconds--
      }
    }, 1000)

    const userId = parseInt(this.$route.query['id']) || null
    if (userId) {
      this.userIdPreSelected = userId
      const currentUrl = window.location.href
      const newUrl = currentUrl.split('?')[0]
      history.replaceState({}, '', newUrl)
    }
  },
  computed: {
    selectedUser() {
      return this.users.find(u => u.id == this.selectedUserId)
    }
  },
  watch: {
    modelValue(user: App.Models.User | null) {
      if (user) {
        this.selectedUserId = user.id
      }
    },
    selectedUserId(val) {
      if (val !== this.modelValue?.id) {
        this.$emit('update:modelValue', this.selectedUser)

        if (this.small && !this.selectedUser.has_pin) {
          this.submit(true)
        }
      }
    }
  },
  methods: {
    async loadUsers() {
      this.loading = 'users'
      const {data: {data}} = await this.$http.get(`/users`, {
        params: {
          itemsPerPage: -1
        }
      }).catch(e => e)
      this.loading = ''

      this.users = data
      if (this.userIdPreSelected) {
        this.selectedUserId = this.userIdPreSelected
        this.userIdPreSelected = false
      }
    },
    startOtpLogin() {
      this.otpLogin = true
      this.sendOtp()
    },
    async sendOtp() {
      this.error = ''
      const { data: { code, message, errors }, status } = await this.$auth.sendOtp({phone: this.selectedUser?.phone, register: false, reset: true}).catch(e => e)

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      if (code) {
        this.pin = code
      }
    },
    async submit(noPin = false) {
      if (this.otpLogin) {
        await this.login()
      } else {
        await this.pinLogin(noPin)
      }
    },
    async pinLogin(noPin = false) {
      if (!this.selectedUserId || this.loading) return

      this.error = ''
      this.loading = 'login'
      const { data: { message, errors }, status } = await this.$auth.pinLogin({
        user_id: this.selectedUserId,
        pin: noPin ? '' : this.pin
      }).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      this.navigateToCalendar()
    },
    async login() {
      if (this.loading) return

      this.error = ''
      this.loading = 'login'
      const { data: { message, errors }, status } = await this.$auth.login({otp: this.pin}).catch(e => e)
      this.loading = ''

      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      // before navigating, show reset pin modal
      this.openDialog('resetPin')
    },
    async resend() {
      this.error = ''
      const { data: { message, errors }, status } = await this.$auth.sendOtp({phone: this.selectedUser?.phone, register: false}).catch(e => e)
      if (this.$error(status, message, errors, false)) {
        this.error = message
        return
      }

      this.resendSeconds = 30
    },
    navigateToCalendar() {
      this.$emit('success')
      this.$router.push(this.$routeTo(this.ROUTES.CALENDAR))
      this.pin = ''
    }
  }
}
</script>
<style lang="scss" scoped>

.account-select-wrapper {
  height: 600px;
  max-height: 600px;
}
</style>
